import styled from '@emotion/styled';

import { OutlineButton, PrimaryButton } from '@/components/common/Button/style';
import { inputBaseStyles, selectBaseStyles } from '@/components/common/form/controlStyles';

import { pageSectionLayout } from '@/styles/pageLayout';

export const Container = styled.main`
	${pageSectionLayout}
	display: flex;
	flex-direction: column;
	gap: 5.6rem;
	box-sizing: border-box;
`;

export const SectionTitle = styled.h2`
	color: ${(props) => props.theme.ui.text.primary};
	font-size: ${(props) => props.theme.fontSize.size4xl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.tight};
`;

export const SearchPanel = styled.section`
	display: flex;
	flex-direction: column;
	gap: 3.2rem;
	padding: 4rem;
	background-color: #f5f9fc;
	border: 1px solid #d7e0e8;

	@media screen and (max-width: 900px) {
		padding: 2rem;
	}
`;

export const SearchGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	column-gap: 3.6rem;
	row-gap: 1.6rem;

	@media screen and (max-width: 1080px) {
		grid-template-columns: 1fr;
	}
`;

export const SearchRow = styled.div`
	display: grid;
	grid-template-columns: 16rem minmax(0, 1fr);
	align-items: center;
	gap: 2rem;

	@media screen and (max-width: 640px) {
		grid-template-columns: 1fr;
		gap: 1rem;
	}
`;

export const SearchLabel = styled.label`
	color: #34495e;
	font-size: 1.8rem;
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	white-space: pre-line;
`;

export const Input = styled.input`
	${(props) => inputBaseStyles(props.theme)};
`;

export const Select = styled.select`
	${(props) => selectBaseStyles(props.theme)};
`;

export const YearControls = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
	align-items: center;
	gap: 1.2rem;
`;

export const DoubleControls = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 1rem;
`;

export const YearDivider = styled.span`
	color: #5f6c7b;
	font-size: 1.8rem;
	line-height: ${(props) => props.theme.lineHeight.tight};
`;

export const SearchButtonRow = styled.div`
	display: flex;
	justify-content: center;
`;

export const SearchButton = styled(PrimaryButton)`
	min-width: 22.6rem;
`;

export const ResultSection = styled.section`
	display: flex;
	flex-direction: column;
	gap: 2rem;
`;

export const ResultTitle = styled.h3`
	color: #212529;
	font-size: 2.4rem;
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.heading};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

export const ResultList = styled.div`
	border: 1px solid #d7e0e8;
	background-color: #fff;
	width: 100%;
`;

export const ResultItem = styled.article`
	padding: 2rem 3rem 1.8rem;
	border-bottom: 1px solid #d7e0e8;

	&:last-of-type {
		border-bottom: none;
	}

	@media screen and (max-width: 900px) {
		padding: 2rem 1.6rem 1.8rem;
	}
`;

export const ArticleTitle = styled.h4`
	color: #213b63;
	font-size: 2rem;
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	word-break: keep-all;
`;

export const ArticleSubtitle = styled.p`
	margin-top: 0.6rem;
	color: #6a7786;
	font-size: 1.8rem;
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	word-break: keep-all;
`;

export const ArticleAuthors = styled.p`
	margin-top: 1rem;
	color: #8b97a6;
	font-size: 1.4rem;
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

export const ArticleMeta = styled.p`
	margin-top: 1.8rem;
	color: #506070;
	font-size: 1.6rem;
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

export const ArticleDoi = styled.p`
	margin-top: 0.8rem;
	color: #506070;
	font-size: 1.6rem;
	line-height: ${(props) => props.theme.ui.lineHeight.body};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
`;

export const ActionRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 1.2rem;
	margin-top: 1.4rem;
`;

export const ActionButton = styled(OutlineButton)`
	min-width: 10.8rem;
	min-height: 4rem;
	padding: 0 1.6rem;
	font-size: ${(props) => props.theme.fontSize.sizeSm};
`;

export const EmptyState = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 24rem;
	border: 1px solid #d7e0e8;
	background-color: #fff;
	color: #6a7786;
	font-size: 1.8rem;
`;

export const AbstractModalContent = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	width: 100%;
	min-height: calc(90vh - 12rem);
	max-height: calc(90vh - 12rem);
`;

export const AbstractModalBody = styled.div`
	flex: 1;
	overflow-y: auto;
	padding-right: 0.4rem;
	color: #34495e;
	font-size: 1.6rem;
	line-height: ${(props) => props.theme.lineHeight.loose};
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.caption};
	word-break: break-word;

	p,
	div,
	span,
	li,
	blockquote {
		color: inherit;
		font-size: inherit;
		line-height: inherit;
	}

	p,
	ul,
	ol,
	blockquote {
		margin: 0 0 1.2rem;
	}

	ul,
	ol {
		padding-left: 2rem;
	}

	.text-tiny {
		font-size: 0.7em !important;
	}

	.text-small {
		font-size: 0.85em !important;
	}

	.text-big {
		font-size: 1.4em !important;
	}

	.text-huge {
		font-size: 1.8em !important;
	}

	figure.table {
		margin: 0 0 1.2rem;
	}

	img {
		max-width: 100%;
		height: auto;
	}

	table {
		width: 100%;
		border-collapse: collapse;
	}

	th,
	td {
		border: 1px solid #cfd7df;
		padding: 1.2rem;
		vertical-align: top;
	}

	th {
		background-color: #f5f8fb;
		font-weight: 600;
	}
`;

export const ModalActionRow = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 1.2rem;
	padding-top: 2rem;
	border-top: 1px solid #d7e0e8;
`;
